import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MODEL_FILTER_TYPES } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import {
  DEFAULT_VIEW_ID,
  MODELS_VIEWS_KEY,
} from 'dashboard409a/pages/Workboard/common/constants';
import { DEFAULT_TRAILING_MONTHS, getTrailingDateRange } from 'dashboard409a/pages/Workboard/common/trailingDates';
import { ModelView } from 'dashboard409a/pages/Workboard/ModelViewsSelector/ModelViewsSelector.props';
import { UserGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import {
  createModelsTableView,
  deleteModelsTableView,
  listModelsTableViews,
  updateModelsTableView,
} from 'dashboard409a/services/workboardTableViews';
import {
  fromTableView,
  syncModelViewsForGroupUpdate,
  toTableViewCreatePayload,
  toTableViewUpdatePayload,
  WORKBOARD_TABLE_VIEWS_QUERY_KEY,
  WORKBOARD_VIEWS_MIGRATED_KEY,
} from 'dashboard409a/types/workboardTableViews';
import {
  DEFAULT_FILTER_TYPE_SELECTION,
  normalizeFilterTypeSelection,
  normalizeModelFilterType,
} from 'dashboard409a/utilities/modelFilterNormalization';
import { NormalizableDate, normalizeDate } from 'utilities/dateUtils';

export const getDefaultModelView = (): ModelView => ({
  id: DEFAULT_VIEW_ID,
  name: 'Default',
  preparerIds: [],
  reviewerIds: [],
  searchStatus: [],
  useTrailingDates: true,
  trailingMonths: DEFAULT_TRAILING_MONTHS,
  ...getTrailingDateRange(),
  filterType: MODEL_FILTER_TYPES.ASSIGNED,
  filterTypeSelection: DEFAULT_FILTER_TYPE_SELECTION,
  columnVisibility: {},
});

const normalizeLocalModelView = (view: ModelView): ModelView => ({
  ...view,
  fromDate: normalizeDate((view as { fromDate?: unknown }).fromDate as NormalizableDate),
  toDate: normalizeDate((view as { toDate?: unknown }).toDate as NormalizableDate),
  filterType: normalizeModelFilterType(view.filterType),
  filterTypeSelection: normalizeFilterTypeSelection(
    view.filterTypeSelection as Record<string, unknown> | undefined,
    view.filterType
  ),
});

const migrateLocalViewsIfNeeded = async (): Promise<void> => {
  const { getPreference, migrateLegacyLocalStorageKey, removePreference, setPreference } = await import(
    'services/storage/preferencesIndexedDB'
  );

  const alreadyMigrated = await getPreference<boolean>(WORKBOARD_VIEWS_MIGRATED_KEY);
  if (alreadyMigrated) {
    return;
  }

  await migrateLegacyLocalStorageKey(MODELS_VIEWS_KEY);
  const localViews = await getPreference<ModelView[]>(MODELS_VIEWS_KEY);
  const userViews = (localViews ?? [])
    .filter(view => view.id !== DEFAULT_VIEW_ID)
    .map(normalizeLocalModelView);

  if (userViews.length === 0) {
    await setPreference(WORKBOARD_VIEWS_MIGRATED_KEY, true);
    return;
  }

  const results = await Promise.allSettled(
    userViews.map(view => createModelsTableView(toTableViewCreatePayload(view)))
  );

  const allSucceeded = results.every(result => result.status === 'fulfilled');
  if (allSucceeded) {
    await removePreference(MODELS_VIEWS_KEY);
    await setPreference(WORKBOARD_VIEWS_MIGRATED_KEY, true);
  }
};

export const useWorkboardTableViews = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: WORKBOARD_TABLE_VIEWS_QUERY_KEY,
    queryFn: async () => {
      await migrateLocalViewsIfNeeded();
      const response = await listModelsTableViews();
      return response.definitions.map(fromTableView);
    },
    staleTime: 30_000,
  });

  const serverViews = useMemo(() => data ?? [], [data]);

  const views = useMemo(
    () => [getDefaultModelView(), ...serverViews],
    [serverViews]
  );

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: WORKBOARD_TABLE_VIEWS_QUERY_KEY }),
    [queryClient]
  );

  const createMutation = useMutation({
    mutationFn: async (view: ModelView) => {
      const created = await createModelsTableView(toTableViewCreatePayload(view));
      return fromTableView(created);
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (view: ModelView) => {
      if (view.id === DEFAULT_VIEW_ID) {
        return view;
      }
      const updated = await updateModelsTableView(view.id, toTableViewUpdatePayload(view));
      return fromTableView(updated);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (viewId: string) => {
      if (viewId === DEFAULT_VIEW_ID) {
        return;
      }
      await deleteModelsTableView(viewId);
    },
    onSuccess: invalidate,
  });

  const cloneMutation = useMutation({
    mutationFn: async ({ source, name }: { source: ModelView; name: string }) => {
      const payload = toTableViewCreatePayload({ ...source, id: 'temp', name });
      const created = await createModelsTableView(payload);
      return fromTableView(created);
    },
    onSuccess: invalidate,
  });

  const createView = useCallback(
    (view: ModelView) => createMutation.mutateAsync(view),
    [createMutation]
  );

  const updateView = useCallback(
    (view: ModelView) => updateMutation.mutateAsync(view),
    [updateMutation]
  );

  const deleteView = useCallback(
    (viewId: string) => deleteMutation.mutateAsync(viewId),
    [deleteMutation]
  );

  const cloneView = useCallback(
    (source: ModelView, name: string) => cloneMutation.mutateAsync({ source, name }),
    [cloneMutation]
  );

  const syncViewsForGroupUpdate = useCallback(
    async (updatedGroup: UserGroup) => {
      const currentServerViews = queryClient.getQueryData<ModelView[]>(WORKBOARD_TABLE_VIEWS_QUERY_KEY) ?? [];
      const synced = syncModelViewsForGroupUpdate(currentServerViews, updatedGroup);
      const changedViews = synced.filter((view, index) => view !== currentServerViews[index]);

      if (changedViews.length === 0) {
        return synced;
      }

      await Promise.all(changedViews.map(view => updateModelsTableView(view.id, toTableViewUpdatePayload(view))));
      await invalidate();
      return synced;
    },
    [queryClient, invalidate]
  );

  return {
    views,
    serverViews,
    isLoading: isLoading || isFetching,
    isReady: !isLoading,
    refetch,
    createView,
    updateView,
    deleteView,
    cloneView,
    syncViewsForGroupUpdate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
